import { useState, useEffect } from 'react';
import * as THREE from 'three';
import { Project } from '../../types';
import { useAppStore } from '../../store/useAppStore';

// Cache global pour les médias
export const videoCache = new Map<string, HTMLVideoElement>();
export const textureCache = new Map<string, THREE.Texture>();
const mediaLoadPromises = new Map<string, Promise<void>>();
const loadedUrls = new Set<string>();

let mediaLoadedCache = false;
const textureLoader = new THREE.TextureLoader();

// Fonction de préchargement global
export async function preloadMedia(url: string): Promise<void> {
  if (loadedUrls.has(url)) return;

  let loadPromise = mediaLoadPromises.get(url);
  if (loadPromise) {
    await loadPromise;
    return;
  }

  if (url.endsWith('.mp4') || url.endsWith('.webm')) {
    loadPromise = loadVideo(url);
  } else {
    loadPromise = loadTexture(url);
  }

  mediaLoadPromises.set(url, loadPromise);
  await loadPromise;
}

const loadTexture = async (url: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (textureCache.has(url)) {
      resolve();
      return;
    }

    textureLoader.load(
      url,
      texture => {
        textureCache.set(url, texture);
        loadedUrls.add(url);
        resolve();
      },
      undefined,
      error => {
        console.error(`Failed to load texture: ${url}`, error);
        reject(error);
      }
    );
  });
};

const loadVideo = async (url: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (videoCache.has(url)) {
      resolve();
      return;
    }

    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    video.loop = true;
    video.crossOrigin = 'anonymous';

    const handleLoad = () => {
      videoCache.set(url, video);
      loadedUrls.add(url);
      resolve();
    };

    const handleError = () => {
      console.error(`Failed to load video: ${url}`);
      reject();
    };

    video.addEventListener('canplaythrough', handleLoad, { once: true });
    video.addEventListener('error', handleError, { once: true });
    video.src = url;
    video.load();
  });
};

export function useMediaLoader(projects: Project[]) {
  const [allMediaLoaded, setAllMediaLoaded] = useState(mediaLoadedCache);
  const { setLoadingProgress, setCurrentPage } = useAppStore();

  useEffect(() => {
    if (mediaLoadedCache) return;

    const loadAllMedia = async () => {
      const allMedia = projects.flatMap(project => {
        const media = [project.thumbnail];
        if (project.projectMedia) {
          media.push(...project.projectMedia);
        }
        return media;
      });

      const total = allMedia.length;
      let loaded = 0;

      try {
        await Promise.all(
          allMedia.map(async url => {
            await preloadMedia(url);
            loaded++;
            setLoadingProgress((loaded / total) * 100);
          })
        );

        mediaLoadedCache = true;
        setAllMediaLoaded(true);
        setLoadingProgress(100);

        setTimeout(() => {
          setCurrentPage('home');
        }, 2000);
      } catch (error) {
        console.error('Error loading media:', error);
      }
    };

    loadAllMedia();

    return () => {
      // Ne pas nettoyer les caches ici, ils doivent persister
    };
  }, [projects, setLoadingProgress, setCurrentPage]);

  return { allMediaLoaded };
}
